import { ShareModal, authentication, logger } from '@ecosystem/newron-client-lib';
import { SharingConfig } from '@ecosystem/newron-client-lib/build/filters/modals';
import { ShareUser } from '@ecosystem/newron-client-lib/build/sharing/types';

import React, { useCallback, useEffect, useState } from 'react';
import {
  SavedCustomFeatureSegments,
  SavedCustomPriceClass,
  SharePermissionsBody,
  UserAccess,
} from '../types/content/custom.types';
import { PreferenceName } from '../types';

export interface ShareCustomModalProps extends SharingConfig {
  /** is the model open or not */
  isOpen: boolean;
  /** the selected saved custom segment to share */
  itemToShare: SavedCustomPriceClass | SavedCustomFeatureSegments;
  /** the PreferenceName */
  preferenceName: PreferenceName;
  /** callback function for closing the modal */
  onClose: () => void;
  /** callback function for when sharing was a success */
  onSuccess?: () => void;
  /** callback function for when sharing failed */
  onShareError?: (error: Error) => void;
  /** function to get the list of currently shared users from preferences endpoint */
  getSharedUsers: (preferenceId: string, preferenceName: PreferenceName) => Promise<UserAccess[]>;
  /** function to share the saved custom segment by updating permissions in preference endpoint */
  shareToUser: (preferenceId: string, permissionsBody: SharePermissionsBody[]) => Promise<void>;
}

export const ShareCustomModal = ({
  isOpen,
  itemToShare,
  preferenceName,
  onClose,
  onSuccess,
  onShareError,
  getUsers,
  getSharedUsers,
  shareToUser,
}: ShareCustomModalProps): JSX.Element => {
  const owner = authentication.getUser();
  const itemType = preferenceName === PreferenceName.segment_price ? 'Price Class' : 'Feature Segment';
  const [currentlySharedUsers, setCurrentlySharedUsers] = useState<ShareUser[]>([]);

  /**
   * Function to get the currently shared users
   */
  const getCurrentlySharedUsers = useCallback(async (): Promise<ShareUser[]> => {
    const userAccess: UserAccess[] = await getSharedUsers(itemToShare.preferenceId, preferenceName);

    return userAccess.map(user => {
      return {
        accessLevel: user.accessLevel,
        email: user.userInfo.email,
        clientId: owner.clientId,
        familyName: user.userInfo.familyName,
        givenName: user.userInfo.givenName,
        userId: user.userInfo.userId,
      };
    });
  }, [itemToShare, owner.userId]);

  // call preferences to get the list of currently shared users
  useEffect(() => {
    if (isOpen) {
      getCurrentlySharedUsers().then(setCurrentlySharedUsers);
    }
  }, [isOpen, owner.userId]);

  /**
   * Function to get the available users you are allowed to share with
   */
  const getAvailableUsers = useCallback(async () => {
    const users = await getUsers();
    return users.map(user => ({
      userId: user.userId,
      givenName: user.givenName,
      familyName: user.familyName,
      email: user.email,
      clientId: owner.clientId,
    }));
  }, [getUsers, owner.clientId]);

  const onCustomSharedSuccess = useCallback(
    (users: ShareUser[]) => {
      onSuccess();
      logger.info(`Successfully shared custom ${itemType.toLowerCase()}. Total viewers: ${users.length}`);
    },
    [onSuccess]
  );

  /**
   * Function to PATCH users to the permissions endpoint
   */
  const onSave = useCallback(
    async (users: ShareUser[]) => {
      const permissionsBody: SharePermissionsBody[] = users.map(user => {
        return {
          userId: user.userId,
          access: user.accessLevel,
        };
      });

      onCustomSharedSuccess(users);
      await shareToUser(itemToShare.preferenceId, permissionsBody);
    },
    [itemToShare]
  );

  const handleGetUsersError = useCallback(
    (err: Error) => {
      logger.error(err, { action: `Get users to share custom ${itemType.toLowerCase()}`, userId: owner.userId });
    },
    [owner.userId]
  );

  const handleShareError = useCallback(
    (e: Error) => {
      logger.error(e, { action: `Sharing custom ${itemType.toLowerCase()}`, userId: owner.userId });
      onShareError && onShareError(e);
    },
    [onShareError]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    currentlySharedUsers.length > 0 && (
      <ShareModal
        itemName={itemToShare?.name}
        getAvailableUsers={getAvailableUsers}
        users={currentlySharedUsers}
        onClose={handleClose}
        onShareError={handleShareError}
        onGetUsersError={handleGetUsersError}
        onSave={onSave}
        isOpen={isOpen}
        onSuccess={onSuccess}
        itemType={itemType}
        sharableClients={[{ clientId: owner.clientId, clientName: owner.clientName }]}
      />
    )
  );
};
